/**
 * Session state.
 *
 * The token lives in localStorage (see `api/client.ts`); this provider owns what the UI
 * knows about it. Two behaviours worth keeping:
 *
 *  1. A token found in storage is *verified* on load rather than trusted. Otherwise a
 *     stale token renders the app shell for a moment before every request bounces.
 *  2. Any 401 anywhere fires `UNAUTHORIZED_EVENT`, which drops the session here. That is
 *     why there is no 401 handling in the pages.
 */

import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react'

import { UNAUTHORIZED_EVENT, getToken, setToken } from '../api/client'
import { auth, type Principal } from '../api/endpoints'
import { AuthContext, type AuthStatus } from './context'

export function AuthProvider({ children }: { children: ReactNode }) {
  // Start in `checking` only if there is something to check — a first-time visitor
  // should never see a spinner for a request that will not be made.
  const [status, setStatus] = useState<AuthStatus>(() => (getToken() ? 'checking' : 'anonymous'))
  const [principal, setPrincipal] = useState<Principal | null>(null)

  useEffect(() => {
    if (!getToken()) return

    let cancelled = false
    auth
      .me()
      .then((who) => {
        if (cancelled) return
        setPrincipal(who)
        setStatus('authenticated')
      })
      .catch(() => {
        if (cancelled) return
        setToken(null)
        setPrincipal(null)
        setStatus('anonymous')
      })

    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => {
    const onUnauthorized = () => {
      setPrincipal(null)
      setStatus('anonymous')
    }
    window.addEventListener(UNAUTHORIZED_EVENT, onUnauthorized)
    return () => window.removeEventListener(UNAUTHORIZED_EVENT, onUnauthorized)
  }, [])

  const login = useCallback(async (username: string, password: string) => {
    const session = await auth.login(username, password)
    setToken(session.access_token)
    // Resolve the identity now so the shell can greet the operator, and so a token that
    // is somehow rejected immediately never reaches the app as "authenticated".
    setPrincipal(await auth.me())
    setStatus('authenticated')
  }, [])

  const logout = useCallback(() => {
    setToken(null)
    setPrincipal(null)
    setStatus('anonymous')
  }, [])

  const value = useMemo(
    () => ({ status, principal, login, logout }),
    [status, principal, login, logout],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
